import { displayName, participantNote } from "../mailFormat";
import { MailPill } from "./MailPill";
import { getParticipantAction } from "../participantLinks";
import type { MailParticipant } from "../types";

const LINK_CLASS =
  "rounded-sm text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/** Contacts and company addresses are shown as pills; colleagues stay as plain names. */
const ParticipantPill = ({ participant }: { participant: MailParticipant }) => {
  const note = participant.type === "contact" ? participantNote(participant) : null;
  const action = getParticipantAction(participant);

  return (
    <MailPill
      tone={participant.type === "company" ? "customer" : "contact"}
      title={participant.email ?? undefined}
      href={action?.kind === "link" ? action.href : undefined}
      onClick={action?.kind === "popup" ? action.open : undefined}
    >
      {displayName(participant)}
      {note && <span className="font-normal opacity-75"> · {note}</span>}
    </MailPill>
  );
};

const ParticipantName = ({ participant }: { participant: MailParticipant }) => {
  if (participant.type !== "user") {
    return <ParticipantPill participant={participant} />;
  }

  const name = displayName(participant);
  const note = participantNote(participant);
  const tooltip = participant.email ?? undefined;
  const action = getParticipantAction(participant);

  const content = (
    <>
      <span className="font-medium text-gray-800">{name}</span>
      {note && <span className="text-gray-500"> ({note})</span>}
    </>
  );

  if (action?.kind === "popup") {
    return (
      <button type="button" title={tooltip} onClick={action.open} className={LINK_CLASS}>
        {content}
      </button>
    );
  }

  if (action?.kind === "link") {
    return (
      <a href={action.href} title={tooltip} className={LINK_CLASS}>
        {content}
      </a>
    );
  }

  return <span title={tooltip}>{content}</span>;
};

type MailParticipantLineProps = {
  label: string;
  participants: MailParticipant[];
  /** Shown when nobody on this line matched a Klipboard CRM record. */
  emptyText?: string;
};

export const MailParticipantLine = ({ label, participants, emptyText }: MailParticipantLineProps) => {
  if (participants.length === 0 && !emptyText) return null;

  return (
    <div className="flex gap-2 text-sm">
      <span className="w-10 shrink-0 pt-px text-gray-500">{label}</span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-1.5 gap-y-1">
        {participants.length === 0 ? (
          <span className="text-gray-500">{emptyText}</span>
        ) : (
          participants.map(participant => (
            <ParticipantName key={`${participant.type}-${participant.id}`} participant={participant} />
          ))
        )}
      </div>
    </div>
  );
};
